import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Reveal } from "../components/Motion";
import Seo from "../components/Seo";
import { getArticle, getArticles } from "../lib/api";

const Article = () => {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [article, setArticle] = useState(null);
  const [more, setMore] = useState([]);

  useEffect(() => {
    getArticle(slug).then(setArticle).catch(() => setArticle(false));
    getArticles().then((all) => setMore(all.filter((a) => a.slug !== slug).slice(0, 3))).catch(() => {});
  }, [slug]);

  if (article === false) {
    return <div className="flex min-h-[60vh] items-center justify-center pt-32"><p className="font-serif text-3xl text-white/60">{t.common.notFound}</p></div>;
  }
  if (!article) {
    return <div className="flex min-h-[60vh] items-center justify-center pt-32"><p className="text-white/40">{t.common.loading}</p></div>;
  }

  return (
    <div data-testid="article-page" className="bg-obsidian">
      <Seo
        title={article.title[lang]}
        description={article.excerpt[lang]}
        path={`/library/${slug}`}
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: article.title[lang],
            description: article.excerpt[lang],
            image: article.cover_image,
            inLanguage: lang,
            author: { "@type": "Organization", name: "MYSYMP AG" },
            publisher: { "@type": "Organization", name: "MYSYMP AG" },
          },
        ]}
      />
      <section className="mx-auto max-w-3xl px-6 pt-36 md:pt-48">
        <Link to="/library" data-testid="article-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/50 hover:text-champagne transition-colors">
          <ArrowLeft size={14} /> {t.library.back}
        </Link>
        <span className="mt-10 block text-[10px] uppercase tracking-[0.3em] text-champagne">{article.read_time} {t.library.readTime}</span>
        <h1 className="mt-5 font-serif text-4xl font-light leading-[1.05] tracking-tight text-white md:text-6xl">{article.title[lang]}</h1>
        <p className="mt-6 text-lg font-light italic leading-relaxed text-white/60">{article.excerpt[lang]}</p>
      </section>

      <section className="mx-auto max-w-4xl px-6 py-16">
        <Reveal>
          <div className="relative aspect-[16/9] overflow-hidden border border-white/10">
            <img src={article.cover_image} alt={article.title[lang]} className="h-full w-full object-cover" />
          </div>
        </Reveal>
      </section>

      <article className="mx-auto max-w-2xl px-6 pb-28">
        {article.body[lang].split("\n\n").map((para, i) => (
          <Reveal key={i} delay={0.05}>
            <p className="mb-8 text-lg font-light leading-[1.9] text-white/75">{para}</p>
          </Reveal>
        ))}
      </article>

      {more.length > 0 && (
        <section className="border-t border-white/10 py-24">
          <div className="mx-auto max-w-[1400px] px-6 md:px-12">
            <h3 className="mb-10 font-serif text-3xl font-light text-white">{t.library.more}</h3>
            <div className="grid gap-8 md:grid-cols-3">
              {more.map((a) => (
                <Link key={a.id} to={`/library/${a.slug}`} className="group block">
                  <div className="relative aspect-[4/3] overflow-hidden border border-white/10">
                    <img src={a.cover_image} alt={a.title[lang]} className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                  </div>
                  <h4 className="mt-4 font-serif text-xl font-light text-white group-hover:text-champagne transition-colors">{a.title[lang]}</h4>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Article;
